import React from 'react';
import { Card, Progress, Tooltip, Typography } from 'antd';
import { COMPETENCY_BY_ID, FRAMEWORKS, MAX_STRUCTURE_SCORE, sectionTiming } from '@evaltick/shared';
import type { CoachDimensionScore, QuestionType, StructureComponentScore } from '@evaltick/shared';
import { formatDurationClock, scoreToColor } from '@/utils';
import styles from './DimensionBreakdown.module.scss';

const { Text } = Typography;

interface DimensionBreakdownProps {
  dimensionScores: CoachDimensionScore[] | null | undefined;
  // `AssessmentResponse.competencyId` is a plain `string` (not the narrower
  // `CompetencySlug`), so accept `string` here to match every caller.
  competencyId: string;
  questionType: QuestionType;
  structureScore: number | null | undefined;
  structureRationale: string | null | undefined;
  structureComponentScores: StructureComponentScore[] | null | undefined;
}

export const DimensionBreakdown: React.FC<DimensionBreakdownProps> = ({
  dimensionScores,
  competencyId,
  questionType,
  structureScore,
  structureRationale,
  structureComponentScores,
}) => {
  if (!dimensionScores || dimensionScores.length === 0) return null;

  // Dimension scores carry only `key`; the human-readable label lives in the
  // static competency config, so look it up by (competency, question type, key).
  const dimensions = COMPETENCY_BY_ID[competencyId]?.questionTypes[questionType]?.dimensions ?? [];
  const labelByKey = Object.fromEntries(dimensions.map((d) => [d.key, d.label]));

  const framework = FRAMEWORKS[questionType];
  const componentLabelByKey = Object.fromEntries(framework.components.map((c) => [c.key, c.label]));

  const structurePercent =
    structureScore != null ? Math.round((structureScore / MAX_STRUCTURE_SCORE) * 100) : null;

  return (
    <Card title="Dimension Breakdown" className={styles.dimensionsCard}>
      <div className={styles.dimensions}>
        {dimensionScores.map((ds: CoachDimensionScore) => (
          <div key={ds.key} className={styles.dimension}>
            <div className={styles.dimensionHeader}>
              <Text strong className={styles.dimensionLabel}>{labelByKey[ds.key] ?? ds.key.replace(/-/g, ' ')}</Text>
              <Tooltip title={ds.rationale}>
                <Text className={styles.dimensionScore}>{ds.score ?? 'Not assessed'}</Text>
              </Tooltip>
            </div>
            {ds.score != null && (
              <Progress
                percent={ds.score}
                strokeColor={scoreToColor(ds.score)}
                showInfo={false}
                size="small"
              />
            )}
            {ds.rationale && (
              <Text type="secondary" className={styles.dimensionRationale}>{ds.rationale}</Text>
            )}
          </div>
        ))}
      </div>

      {(structurePercent != null || (structureComponentScores && structureComponentScores.length > 0)) && (
        <div className={styles.structureBreakdown}>
          <Text strong className={styles.structureSectionTitle}>{framework.name} Structure Breakdown</Text>

          {structurePercent != null && (
            <div className={styles.structureDimension}>
              <div className={styles.dimensionHeader}>
                <Text strong className={styles.dimensionLabel}>Answer Structure ({framework.name})</Text>
                <Text className={styles.dimensionScore}>{structureScore} / {MAX_STRUCTURE_SCORE}</Text>
              </div>
              <Progress
                percent={structurePercent}
                strokeColor={scoreToColor(structurePercent)}
                showInfo={false}
                size="small"
              />
              {structureRationale && (
                <Text type="secondary" className={styles.dimensionRationale}>{structureRationale}</Text>
              )}
            </div>
          )}

          {structureComponentScores && structureComponentScores.length > 0 && (
            <div className={styles.dimensions}>
              {structureComponentScores.map((s: StructureComponentScore) => {
                const componentPercent = Math.round((s.score / MAX_STRUCTURE_SCORE) * 100);
                const timing = sectionTiming(s);
                return (
                  <div key={s.key} className={styles.dimension}>
                    <div className={styles.dimensionHeader}>
                      <Text strong className={styles.dimensionLabel}>
                        {componentLabelByKey[s.key] ?? s.key.replace(/-/g, ' ')}
                      </Text>
                    </div>
                    <Progress
                      percent={componentPercent}
                      strokeColor={scoreToColor(componentPercent)}
                      showInfo={false}
                      size="small"
                    />
                    <Text type="secondary" className={styles.dimensionRationale}>{s.rationale}</Text>
                    {timing && (
                      <Text className={styles.sectionTiming}>
                        ~{formatDurationClock(timing.ms)}{timing.estimated ? ' (estimated)' : ''}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
